import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { ApiParamsType } from '@/features/shared/types/ApiParamsType';
import { OneRosterApiTyped } from '@/lib/http.server';
import { standardMiddlewares } from '@/middlewares/standardMiddlewares';
import { assertUserAuthenticated } from '@/middlewares/userAuthMiddleware';

import { Organization } from '../validators/schemas';

export type OrganizationResponse = {
  organization: Organization;
};

type GetOrganizationResponse = {
  data: OrganizationResponse;
  success: boolean;
  error: string | null;
};

const getOrganizationSchema = z.object({
  sourcedId: z.string(),
});

const getOrganizationDefaultParamsValues = {
  fields:
    'sourcedId,name,status,type,identifier,metadata,parent,children,dateLastModified',
  filter: `status='active'`,
  limit: 1000,
} as const;

export const getOrganizationDefaultParams =
  getOrganizationDefaultParamsValues satisfies ApiParamsType;

const fetchOnerosterOrganization = async (
  onerosterApi: OneRosterApiTyped,
  sourcedId: string
) => {
  const { data } = await onerosterApi.GET(
    '/ims/oneroster/rostering/v1p2/orgs/{sourcedId}',
    {
      params: {
        path: { sourcedId },
      },
    }
  );

  return {
    organization: data?.org,
  };
};

export const getOrganization = createServerFn({
  method: 'POST',
})
  .middleware([standardMiddlewares])
  .validator(getOrganizationSchema)
  .handler(async ({ context, data }): Promise<GetOrganizationResponse> => {
    const { cognitoUser, oneRosterApiTyped } = context;

    assertUserAuthenticated(cognitoUser);

    const response = await fetchOnerosterOrganization(
      oneRosterApiTyped,
      data.sourcedId
    );

    return {
      success: true,
      data: response,
      error: null,
    } as GetOrganizationResponse;
  });
